import React, { useEffect, useMemo, useState } from "react";
import { TrophyIcon, XMarkIcon } from "@heroicons/react/24/outline";
import { motion, AnimatePresence } from "framer-motion";
import ApiCustom from "../../utils/ApiCustom";
import CustomTableComponent from "../../components/CustomTableComponent";
import clsx from "clsx";

// Animation config
const animation = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { duration: 0.3 } },
  exit: { opacity: 0, y: 10, transition: { duration: 0.2 } },
};

// ======================= Modal chi tiết điểm theo KIỂU MỚI =======================
const CompanyPointDetailModal = ({ company, year, isUnpaid, onClose }) => {
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]); // tất cả tiêu chí (auto + manual), sort theo order
  const [serverPoints, setServerPoints] = useState({}); // { [categoryId]: points } theo NĂM
  const [serverNotes, setServerNotes] = useState({}); // { [categoryId]: note } theo NĂM
  const [totalPoints, setTotalPoints] = useState(0);

  // Load categories
  const loadCategories = async () => {
    const res = await ApiCustom.get(`/point-categories`, {
      params: { page: 1, limit: 1000, s: "" },
    });
    const all = res.data?.data?.data || res.data?.data || [];
    const sorted =
      [...all].sort((a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id - b.id) ||
      [];
    setCategories(sorted);
  };

  // Load điểm hiện tại theo NĂM
  const loadPartnerPoints = async () => {
    try {
      const res = await ApiCustom.get(`/partner-points/${company?.id}`, {
        params: { year },
      });
      const payload = res.data?.data || {};
      const items = payload.items || [];
      const pts = {};
      const notes = {};
      items.forEach((it) => {
        if (it?.pointCategoryId == null) return;
        pts[it.pointCategoryId] = Number(it.points || 0);
        if (it.note != null) notes[it.pointCategoryId] = String(it.note);
      });
      setServerPoints(pts);
      setServerNotes(notes);
      setTotalPoints(Number(payload.totalPoints || 0));
    } catch {
      setServerPoints({});
      setServerNotes({});
      setTotalPoints(0);
    }
  };

  useEffect(() => {
    if (!company?.id) return;
    if (isUnpaid) {
      // chưa đóng hội phí: không tải dữ liệu, chỉ hiện thông báo
      setLoading(false);
      return;
    }
    (async () => {
      setLoading(true);
      try {
        await loadCategories();
        await loadPartnerPoints();
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [company?.id, year, isUnpaid]);

  return (
    <AnimatePresence>
      <motion.div
        {...animation}
        className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
        onClick={onClose}
      >
        <motion.div
          {...animation}
          className="relative bg-white w-full max-w-3xl mx-4 rounded-2xl p-7 shadow-xl border border-amber-100 flex flex-col"
          style={{ maxHeight: "92vh" }}
          onClick={(e) => e.stopPropagation()}
        >
          <button
            onClick={onClose}
            className="absolute top-5 right-5 text-gray-400 hover:text-gray-600"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>

          <div className="mb-2">
            <h2 className="text-lg font-semibold mb-1">
              Điểm hoạt động: {company?.companyName || ""}
            </h2>
          </div>

          {isUnpaid ? (
            <div className="border rounded-lg p-4 pb2 bg-amber-50 text-amber-700">
              Hội viên chưa đóng hội phí năm {year}. Không có thống kê hiển thị.
            </div>
          ) : loading ? (
            <div className="text-center py-8 text-gray-400">Đang tải...</div>
          ) : (
            <>
              <div className="mb-4">
                <div className="text-gray-600">Tổng điểm năm {year}:</div>
                <span className="text-2xl font-bold text-amber-700">
                  {totalPoints}
                </span>
              </div>

              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-auto border rounded-lg">
                <table className="table w-full">
                  <thead className="sticky top-0 bg-white">
                    <tr>
                      <th>Tiêu chí</th>
                      <th className="w-28 text-right">Điểm</th>
                      <th className="w-1/2">Ghi chú</th>
                    </tr>
                  </thead>
                  <tbody>
                    {categories.map((c) => {
                      const cid = c.id;
                      const point = Number(serverPoints[cid] || 0);
                      const note = serverNotes[cid] ?? "";
                      return (
                        <tr key={cid}>
                          <td className="align-top">
                            <div className="font-medium">{c.name}</div>
                            <div className="text-xs text-gray-500">
                              Mã: {c.key}
                            </div>
                          </td>
                          <td className="align-top text-right font-semibold">
                            <span
                              className={clsx(
                                point > 0
                                  ? "text-amber-700"
                                  : point < 0
                                    ? "text-gray-400"
                                    : "text-gray-600",
                              )}
                            >
                              {point}
                            </span>
                          </td>
                          <td className="align-top">
                            {note ? (
                              <div className="text-sm text-gray-700">
                                {note}
                              </div>
                            ) : (
                              <span className="text-sm text-gray-400">—</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                    {categories.length === 0 && (
                      <tr>
                        <td
                          colSpan={3}
                          className="text-center text-gray-500 py-6"
                        >
                          Chưa có tiêu chí
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

// =============================== MAIN CARD ===============================
const CompanyActivityPointCard = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Năm lọc (kiểu mới)
  const currentYear = new Date().getFullYear();
  const [year, setYear] = useState(currentYear);

  // Tính toán theo năm
  const [totalsMap, setTotalsMap] = useState({}); // { [companyId]: totalPoints (theo năm) }
  const [unpaidSet, setUnpaidSet] = useState(new Set()); // tập hội viên CHƯA đóng hội phí trong năm

  // Modal chi tiết
  const [detailCompany, setDetailCompany] = useState(null);

  // Load danh sách hội viên
  const loadCompanies = async () => {
    setLoading(true);
    try {
      const res = await ApiCustom.get("company-shows");
      const users = res.data?.data?.data?.users || [];
      const filteredUsers = users.filter(
        (user) => String(user?.approvalStatus) === "Đã duyệt",
      );
      setMembers(filteredUsers);
    } catch {
      setMembers([]);
    } finally {
      setLoading(false);
    }
  };

  // Lấy danh sách hội viên chưa đóng hội phí theo năm
  const loadUnpaidForYear = async (y) => {
    try {
      const r = await ApiCustom.get(`/membership-fee-shows`);
      const fees = r.data?.data?.data || [];

      const feesInYear = fees.filter((f) => {
        const t = new Date(f.createdAt || f.payStartDay || 0);
        return !isNaN(t) && t.getFullYear() === y;
      });

      if (!feesInYear.length) {
        // Không có đợt hội phí trong năm -> coi như không chặn
        setUnpaidSet(new Set());
        return;
      }

      const lists = await Promise.all(
        feesInYear.map((f) =>
          ApiCustom.get(`/membership-fee-not-paid/${f.id}`)
            .then((res) => res.data?.data?.data?.companies || [])
            .catch(() => []),
        ),
      );
      const ids = new Set();
      lists.flat().forEach((c) => c?.id && ids.add(c.id));
      setUnpaidSet(ids);
    } catch {
      setUnpaidSet(new Set());
    }
  };

  // Tổng điểm theo năm cho từng hội viên
  const loadTotalsForYear = async (list, y) => {
    try {
      const entries = await Promise.all(
        (list || []).map(async (c) => {
          try {
            const r = await ApiCustom.get(`/partner-points/${c.id}`, {
              params: { year: y },
            });
            const payload = r.data?.data || {};
            const total = Number(payload.totalPoints || 0);
            return [c.id, total];
          } catch {
            return [c.id, 0];
          }
        }),
      );
      setTotalsMap(Object.fromEntries(entries));
    } catch {
      setTotalsMap({});
    }
  };

  useEffect(() => {
    loadCompanies();
  }, []);

  useEffect(() => {
    loadUnpaidForYear(year);
  }, [year]);

  useEffect(() => {
    if (members.length) loadTotalsForYear(members, year);
    else setTotalsMap({});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [members, year]);

  // Danh sách DN có điểm > 0 VÀ đã đóng hội phí của năm
  const membersWithPoint = useMemo(() => {
    return members.filter(
      (m) => !unpaidSet.has(m.id) && Number(totalsMap[m.id] || 0) > 0,
    );
  }, [members, totalsMap, unpaidSet]);

  const totalMembers = members.length;
  const withPoint = membersWithPoint.length;

  // Bảng danh sách DN có điểm (theo năm)
  const headers = [
    {
      key: "userAvatar",
      label: "Logo",
      render: (item) => (
        <div className="w-10 h-10 rounded-lg overflow-hidden mx-auto bg-gray-50">
          {item?.userAvatar ? (
            <img
              src={item.userAvatar}
              alt={item.companyName}
              className="w-full h-full object-contain"
              loading="lazy"
            />
          ) : (
            <img
              src={`https://ui-avatars.com/api/?name=${item?.companyName}&size=200&background=random&color=fff&bold=true`}
              alt="Profile"
              className="w-full h-full object-contain"
              loading="eager"
              onError={(e) => {
                e.target.onerror = null;
                e.target.src = `https://ui-avatars.com/api/?name=${item?.companyName}&size=200&background=random&color=fff&bold=true`;
              }}
            />
          )}
        </div>
      ),
    },
    {
      key: "companyName",
      label: "Tên đơn vị",
      render: (item) => (
        <div>
          <p className="font-medium text-gray-900">{item.companyName}</p>
          <p className="text-xs text-gray-500">
            {item?.users?.find(
              (e) => e?.CompaniesHasUsers?.isUserRepresentOfCompany,
            )?.userFullName || "—"}
          </p>
        </div>
      ),
    },
    { key: "userPhone", label: "Số điện thoại" },
    { key: "userEmail", label: "Email" },
    {
      key: "point",
      label: `Tổng điểm (${year})`,
      render: (item) => (
        <span className="font-semibold text-amber-700 flex justify-center">
          {Number(totalsMap[item.id] || 0)}
        </span>
      ),
    },
  ];

  return (
    <div className="w-full">
      {/* Card hiển thị nhanh */}
      <motion.div
        {...animation}
        whileHover={{ scale: 1.03 }}
        className="w-full shadow-md sm:w-auto flex flex-col items-center text-center p-5 rounded-2xl border border-amber-100 bg-white hover:border-amber-300 transition-all cursor-pointer"
        onClick={() => setIsOpen(true)}
      >
        <div className="p-3 bg-amber-50 rounded-full mb-3">
          <TrophyIcon className="h-6 w-6 text-amber-500" />
        </div>
        <p className="text-lg text-black mb-1">
          Doanh nghiệp có điểm hoạt động ({year})
        </p>
        <p className="text-3xl font-bold ">
          {loading ? (
            "..."
          ) : (
            <>
              <span className="">{withPoint}</span>
              <span className="mx-1 ">/</span>
              <span>{totalMembers}</span>
            </>
          )}
        </p>
      </motion.div>

      {/* Modal danh sách DN có điểm theo NĂM (đã lọc đóng hội phí) */}
      <AnimatePresence>
        {isOpen && (
          <motion.div
            {...animation}
            className="fixed inset-0 z-50 bg-black/30 backdrop-blur-sm flex items-center justify-center"
            onClick={() => setIsOpen(false)}
          >
            <motion.div
              {...animation}
              className="relative bg-white w-full max-w-3xl mx-4 rounded-2xl p-6 shadow-xl border border-amber-100 flex flex-col"
              style={{ maxHeight: "92vh" }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                onClick={() => setIsOpen(false)}
                className="absolute top-4 right-4 text-gray-400 hover:text-gray-600"
              >
                <XMarkIcon className="h-5 w-5" />
              </button>

              <div className="mr-4 mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
                <h2 className="text-lg font-semibold ">
                  Danh sách doanh nghiệp có điểm ({year})
                </h2>

                <div className="flex items-center gap-2">
                  <span className="text-sm text-gray-600">Năm:</span>
                  <select
                    className="select select-bordered select-sm"
                    value={year}
                    onChange={(e) => setYear(Number(e.target.value))}
                  >
                    {Array.from(
                      { length: 6 },
                      (_, i) => new Date().getFullYear() - i,
                    ).map((y) => (
                      <option key={y} value={y}>
                        {y}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {loading ? (
                <div className="text-center py-6 text-gray-400">
                  Đang tải...
                </div>
              ) : (
                <div className="flex-1 min-h-0 overflow-y-auto">
                  <CustomTableComponent
                    headers={headers}
                    data={membersWithPoint}
                    onRowClick={(item) => setDetailCompany(item)}
                  />
                  {membersWithPoint.length === 0 && (
                    <div className="text-center py-6 text-gray-400">
                      Chưa có doanh nghiệp nào có điểm trong năm {year} (hoặc
                      chưa đóng hội phí).
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Modal chi tiết điểm hội viên (KIỂU MỚI) */}
      <AnimatePresence>
        {detailCompany && (
          <CompanyPointDetailModal
            company={detailCompany}
            year={year}
            isUnpaid={unpaidSet.has(detailCompany.id)}
            onClose={() => setDetailCompany(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default CompanyActivityPointCard;
